import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { AlumniProfile, SearchAlumniRequest, SearchAlumniResponse, SubmitProfileAlumniRequest } from '../types/api';

// API Response Types
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: number;
  success: boolean;
}

export interface ApiMetaResponse<T = any> {
  meta: {
    code: number;
    message: string | null;
    error: string | null;
  };
  data: T;
}

// Update the PaginatedResponse interface
export interface PaginatedResponse<T> {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  data: T[];
  first_page_url: string;
  from: number;
  last_page_url: string;
  links: Array<{
    url: string | null;
    label: string;
    active: boolean;
  }>;
  next_page_url: string | null;
  path: string;
  prev_page_url: string | null;
  to: number;
}

// Update the PaginatedResponse interface
export interface PaginatedProfileResponse<T> {
  profiles: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    data: T[];
    first_page_url: string;
    from: number;
    last_page_url: string;
    links: Array<{
      url: string | null;
      label: string;
      active: boolean;
    }>;
    next_page_url: string | null;
    path: string;
    prev_page_url: string | null;
    to: number;
  }
}

export interface ProgramBeasiswaResponse<T> {
  items: T[];
  total: number;
}

export interface ProgramBeasiswa {
  value: string;
  label: string;
}

// Request Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

// Response Types
export interface User {
  id: number;
  name: string;
  email: string;
  avatar?: string;
  created_at: string;
  updated_at: string;
}

export interface Thesis {
  id: number;
  title: string;
  abstract: string;
  author: string;
  scientific_field: string;
  language: string;
  publish_date: string;
  country: string;
  file_url?: string;
}

export interface ScientificField {
  id: number;
  name: string;
  code: string;
}

export interface Language {
  id: number;
  name: string;
  code: string;
}

export interface ThesisFeedback {
  id: number;
  thesis_id: number;
  user_id: number;
  feedback: string;
  rating: number;
  created_at: string;
}

// Query Parameters
export interface ThesisQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  scientific_field_id?: number;
  language_id?: number;
  country?: string;
  publish_date_from?: string;
  publish_date_to?: string;
}

export interface ScientificFieldQueryParams {
  search?: string;
  page?: number;
  per_page?: number;
}

// API Configuration
export interface ApiConfig {
  baseURL: string;
  timeout: number;
  headers?: Record<string, string>;
  simulateDelay?: number; // Add this for testing
}

// Add these type definitions after the existing interfaces

export interface MasterTableItem {
  id: string;
  title_id: string;
  title_en: string;
  deskripsi_id: string;
  deskripsi_en: string;
  image: string;
  is_active: string | number;
  tipe: string;
  created_by: number;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface MasterTableQueryParams {
  tipe?: string;
  page?: number;
  per_page?: number;
  search?: string;
}

// Add TalentaCardItem interface
export interface TalentaCardItem {
  id: string;
  title_id: string;
  deskripsi_id: string;
  link_text_id: string;
  link_url_id: string;
  image_id: string;
  is_active: string;
}

// Add TalentaCardQueryParams interface
export interface TalentaCardQueryParams {
  tipe?: string;
  page?: number;
  per_page?: number;
  search?: string;
  program?: string;
  q?: string;
}

export interface BlogQueryParams {
  tipe?: string;
  page?: number;
  per_page?: number;
  search?: string;
}

export interface BlogSectionData  {
  news: BlogItem[],
  highlight: BlogItem,
}

export interface BlogItem {
  id: string;
  kategori_berita_id: number;
  upload_dokumen: string | null;
  upload_thumbnail: string | null;
  judul: string;
  abstrak: string;
  link_youtube: string;
  urutan: number;
  is_highlight: number;
  is_berita: number;
  is_blog: number;
  is_active: number;
  created_by: number | null;
  updated_by: number | null;
  deleted_by: number | null;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  nama_berita: string;
  kategori: {
    id: number;
    nama_kategori: string;
    urutan: number;
    is_active: number;
    created_by: number;
    updated_by: number;
    deleted_by: number | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  };
}

export interface BlogCategory {
  id: string;
  nama_kategori: string;
  urutan: number;
  is_active: number;
  jumlah_berita?: number;
}

export interface BlogPageData {
  news: BlogItem[],
  highlight: BlogItem,
  terbaru: BlogItem[],
  categories: BlogCategory[],
}


export interface CoPHomeResponse {
  banners: Array<{
    id: number;
    upload_banner: string;
    url: string | null;
    is_active: number;
    created_by: number;
    updated_by: number;
    deleted_by: number | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  }>;
  kategori_berita: Array<{
    id: string;
    nama_kategori: string;
    urutan: number;
    is_active: number;
    created_by: number;
    updated_by: number;
    deleted_by: number | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  }>;
  youtube: Array<{
    id: number;
    judul: string;
    deskripsi: string;
    upload_thumbnail: string;
    link_youtube: string;
    is_active: number;
    created_by: number;
    updated_by: number;
    deleted_by: number | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  }>;
}

export interface CoPNewsItem {
    id: string;
    kategori_berita_id: number;
    upload_dokumen: string;
    upload_thumbnail: string;
    judul: string;
    abstrak: string;
    link_youtube: string;
    urutan: number;
    is_highlight: number;
    is_active: number;
    created_by: number;
    updated_by: number;
    deleted_by: number | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    nama_berita: string;
    kategori: {
        id: number;
        nama_kategori: string;
        urutan: number;
        is_active: number;
        created_by: number;
        updated_by: number;
        deleted_by: number | null;
        created_at: string;
        updated_at: string;
        deleted_at: string | null;
    };
}

/** CoP/Blog detail view response (same API as CoP Detail page) */
export interface COPDetailRelatedArticle {
    id: number | string;
    judul: string;
    upload_thumbnail: string;
    traffic_count?: number;
}

export interface COPDetailData {
    id: number | string;
    judul: string;
    abstrak: string;
    upload_dokumen?: string | null;
    upload_thumbnail?: string | null;
    link_youtube?: string | null;
    traffic_count?: number;
    created_at?: string;
    kategori?: {
        id: number;
        nama_kategori: string;
        [key: string]: unknown;
    };
    related_articles?: COPDetailRelatedArticle[];
}

export interface OverviewResponse {
  code: number;
  success: boolean;
  data: Array<{
    "page-setting": {
      section_testimony_name: string;
      section_testimony_text: string;
      section_about_video_id: string;
      section_about_video_thumbnail: string;
      section_about_title: string;
      section_about_content: string;
      section_about_tanggal_update: string;
      section_about_details: Array<{
        title: string;
        label_name: string;
        amount: string;
        order: string;
      }>;
      section_live_video_id: string;
      section_live_title: string;
      section_live_short_description: string;
      section_live_speaker_name: string;
      section_live_speaker_title: string;
      section_live_event_date: string;
      section_live_start_time: string;
      is_display_live: number;
      created_at: string;
      updated_at: string;
      section_about_alumni: number;
    };
    banner: Array<{
      banner_image: string;
      banner_image_mobile: string;
      banner_video: string;
      title: string;
      button_url: string;
      button_text: string;
      text_color: string;
      order: number;
      publish: number;
      created_at: string;
      updated_at: string;
    }>;
    information: Array<{
      icon: string;
      title: string;
      description: string;
      button_url: string;
      order: number;
      publish: number;
      created_at: string;
      updated_at: string;
    }>;
    "pop-up": {
      image: string;
      title: string;
      description: string;
      button_text: string;
      button_url: string;
      publish: number;
      created_at: string;
      updated_at: string;
    };
    testimony: Array<{
      image: string;
      name: string;
      institution_name: string;
      content: string;
      uploaded_date: string;
      category: string;
      publish: number;
      created_at: string;
      updated_at: string;
    }>;
    story: Array<{
      news_category: string;
      title: string;
      slug: string;
      description: string;
      writer_name: string;
      photographer_name: string;
      content: string;
      image: string;
      thumbnail_image: string;
      caption_image: string;
      caption_image_thumbnail: string;
      is_highlight: number;
      publish: number;
      created_at: string;
      updated_at: string;
    }>;
  }>;
}

export interface ImpactResponse {
  meta: {
    code: number;
    message: string | null;
    error: string | null;
  },
  data: {
    "sektor_Sektor Sosial": string,
    "sektor_Sektor Publik": string,
    "sektor_Sektor Privat": string,
    "sektor_Belum Update": string,
    "sektor_Belum Bekerja": string,
    "sektor_Publik/PNS/Kementerian/Pemerintah": number,
    "bidang_Universitas/Perguruan Tinggi/Sekolah Tinggi/Akademi": string,
    "bidang_Swasta": string,
    "bidang_Publik/PNS/Kementerian/Lembaga": string,
    "bidang_Sekolah SD/SMP/SMA": string,
    "bidang_Belum Update": number,
    "bidang_BUMN/BUMD": string,
    "bidang_Wirausaha/Entrepreneur": string,
    "bidang_Belum Bekerja": number,
    "bidang_Masyarakat/LSM/NGO": string,
    "bidang_TNI/POLRI": string,
    "bidang_Pemerintah": string,
    "update_terakhir": string,
  }
}

export interface Alumni {
  id: number,
  no_induk_beasiswa: string | null;
  nik: string;
  kode_reg: string | null;
  nama_lengkap: string;
  gelar_akademik_depan: string;
  gelar_akademik_belakang: string;
  foto_profil: string | null;
  linkedin_profile: string;
  program_beasiswa: string;
  universitas: string;
  negara: string;
  tahun_lulus: string;
  bidang_studi: string;
  pekerjaan_saat_ini: string;
  perusahaan_instansi: string;
  prestasi_utama: string;
  kontribusi_masyarakat: string;
  biography: string;
  konten_flipbook_1: string;
  konten_flipbook_2: string;
  status: string;
  is_starred: boolean;
  submitted_by: string;
  approved_by: string;
  approved_at: string;
  rejected_reason: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  foto_profil_url: string | null;
  batch_pk: string;
  gelar: string;
  jurusan: string;
  jenjang_studi: string;
}

class ApiErrorResponse extends Error {
  constructor(
    public status: number,
    public message: string,
    public errors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiErrorResponse';
  }
}

// Add this utility function at the top of the file
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export class LandingPageApi {
  private client: AxiosInstance;
  private apiToken: string | null = null;
  private simulateDelay: number = process.env.NODE_ENV === 'development' ? 2000 : 0; // Add this property

  constructor(config?: Partial<ApiConfig>) {
    const defaultConfig: ApiConfig = {
      baseURL: process.env.REACT_APP_BASE_API_URL || '',
      timeout: 31000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      simulateDelay: process.env.NODE_ENV === 'development' ? 2000 : 0, // Default no delay
    };

    const finalConfig = { ...defaultConfig, ...config };
    this.simulateDelay = finalConfig.simulateDelay || 0;
    
    this.client = axios.create(finalConfig);
    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Get token from localStorage if not set in instance
        const token = localStorage.getItem('apiToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        if (error.response) {
          const { status, data } = error.response;
          // Handle the meta response structure
          const message = data.meta?.message || data.message || 'An error occurred';
          const errors = data.meta?.error ? { general: [data.meta.error] } : data.errors;
          throw new ApiErrorResponse(
            status,
            message,
            errors
          );
        }
        throw new ApiErrorResponse(0, error.message || 'Network error');
      }
    );
  }

  // Token management
  public setToken(token: string): void {
    this.apiToken = token;
    localStorage.setItem('apiToken', token);
  }

  public getToken(): string | null {
    if (!this.apiToken) {
      this.apiToken = localStorage.getItem('apiToken');
    }
    return this.apiToken;
  }

  public clearToken(): void {
    this.apiToken = null;
    localStorage.removeItem('apiToken');
  }

  // Generic request methods
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      // Simulate delay if configured
      if (this.simulateDelay > 0) {
        await sleep(this.simulateDelay);
      }
      
      const response = await this.client.request<T>(config);
      return response.data;
    } catch (error) {
      if (error instanceof ApiErrorResponse) {
        throw error;
      }
      throw new ApiErrorResponse(0, 'Unknown error occurred');
    }
  }

  private async get<T>(url: string, params?: any): Promise<T> {
    return this.request<T>({ method: 'GET', url, params });
  }

  private async post<T>(url: string, data?: any): Promise<T> {
    return this.request<T>({ method: 'POST', url, data });
  }

  private async put<T>(url: string, data?: any): Promise<T> {
    return this.request<T>({ method: 'PUT', url, data });
  }

  private async delete<T>(url: string): Promise<T> {
    return this.request<T>({ method: 'DELETE', url });
  }

  // Authentication methods
  public async login(data: LoginRequest): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.post<ApiResponse<{ user: User; token: string }>>('/auth/login-tesis', data);
    if (response.success && response.data.token) {
      this.setToken(response.data.token);
    }
    return response;
  }

  public async loginBackdoor(data: LoginRequest): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.post<ApiResponse<{ user: User; token: string }>>('/auth/admin-login-as-alumni', data);
    if (response.success && response.data.token) {
      this.setToken(response.data.token);
    }
    return response;
  }

  public async register(data: RegisterRequest): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.post<ApiResponse<{ user: User; token: string }>>('/auth/register-tesis', data);
    if (response.success && response.data.token) {
      this.setToken(response.data.token);
    }
    return response;
  }

  public async changePassword(data: ChangePasswordRequest): Promise<ApiResponse<void>> {
    return this.post<ApiResponse<void>>('/change-password', data);
  }

  public async forgotPassword(data: ForgotPasswordRequest): Promise<ApiResponse<void>> {
    return this.post<ApiResponse<void>>('/auth/forgot-password', data);
  }

  // Thesis methods
  public async getThesis(params?: ThesisQueryParams): Promise<ApiResponse<PaginatedResponse<Thesis>>> {
    return this.get<ApiResponse<PaginatedResponse<Thesis>>>('/list/tesis', params);
  }

  public async getThesisAdmin(params?: ThesisQueryParams): Promise<ApiResponse<PaginatedResponse<Thesis>>> {
    return this.get<ApiResponse<PaginatedResponse<Thesis>>>('/list/tesis/admin', params);
  }

  public async getThesisDetail(id: number): Promise<ApiResponse<Thesis>> {
    return this.get<ApiResponse<Thesis>>(`/detail/tesis/${id}`);
  }

  public async getTotalThesis(): Promise<ApiResponse<{ total: number }>> {
    return this.get<ApiResponse<{ total: number }>>('/list/total-tesis');
  }

  public async getTotalThesisPublic(): Promise<ApiResponse<{ total: number }>> {
    return this.get<ApiResponse<{ total: number }>>('/list/total-tesis-publik');
  }

  public async getThesisFile(id: number): Promise<Blob> {
    const response = await this.client.get(`/detail/tesis/enkrip/${id}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Scientific fields and languages
  public async getScientificField(params?: ScientificFieldQueryParams): Promise<ApiResponse<PaginatedResponse<ScientificField>>> {
    return this.get<ApiResponse<PaginatedResponse<ScientificField>>>('/list/bidang-keilmuan', params);
  }

  public async getLanguage(params?: ScientificFieldQueryParams): Promise<ApiResponse<PaginatedResponse<Language>>> {
    return this.get<ApiResponse<PaginatedResponse<Language>>>('/list/bahasa', params);
  }

  // Feedback methods
  public async getThesisFeedback(thesisId: number): Promise<ApiResponse<ThesisFeedback[]>> {
    return this.get<ApiResponse<ThesisFeedback[]>>(`/list/feedback/${thesisId}`);
  }

  public async storeThesisFeedback(data: { thesis_id: number; feedback: string; rating: number }): Promise<ApiResponse<ThesisFeedback>> {
    return this.post<ApiResponse<ThesisFeedback>>('/tesis/feedback', data);
  }

  // Profile methods
  public async getProfile(): Promise<ApiMetaResponse<AlumniProfile>> {
    return this.get<ApiMetaResponse<AlumniProfile>>('/profile/v2');
  }

  public async updateProfile(data: Partial<AlumniProfile>): Promise<ApiResponse<AlumniProfile>> {
    return this.put<ApiResponse<AlumniProfile>>('/user/user_detail/update/v2', data);
  }

  public async updateFotoProfile(file: File): Promise<ApiResponse<{ avatar_url: string }>> {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await this.client.post('/user/user_detail/upload-image-tesis', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Registration methods
  public async registerThesis(data: any): Promise<ApiResponse<void>> {
    return this.post<ApiResponse<void>>('/register-peneliti-tesis', data);
  }

  // Statistics methods
  public async getTotalScolarship(): Promise<ApiResponse<{ total: number }>> {
    return this.get<ApiResponse<{ total: number }>>('/list/total/beasiswa');
  }

  public async getOverviewData(): Promise<OverviewResponse> {
    return this.get<OverviewResponse>('/list/total/beasiswa');
  }

  public async getImpactData(): Promise<ImpactResponse> {
    return this.get<ImpactResponse>('/list/total-tesis-publik');
  }

  // Privacy policy methods
  public async getPrivacyPolicy(data: { user_id: number }): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('/privacy-policy', data);
  }

  public async storePrivacyPolicy(data: { user_id: number; agreed: boolean }): Promise<ApiResponse<void>> {
    return this.post<ApiResponse<void>>('/privacy-policy/agree', data);
  }

  // Date and country methods
  public async getPublishDate(): Promise<ApiResponse<string[]>> {
    return this.get<ApiResponse<string[]>>('/list/tesis/date/publish');
  }

  public async getCountry(): Promise<ApiResponse<string[]>> {
    return this.get<ApiResponse<string[]>>('/list/tesis/country/publish');
  }

  // Template methods
  public async getTemplateMitra(type: string): Promise<ApiResponse<{ url: string }>> {
    return this.get<ApiResponse<{ url: string }>>(`/template-proposal/url-by-tipe/${type}`);
  }

  public async getTemplateFile(): Promise<ApiResponse<any[]>> {
    return this.get<ApiResponse<any[]>>('/template-proposal/list-with-urls');
  }

  // Master table methods
  public async getMasterTable(params?: MasterTableQueryParams): Promise<ApiResponse<PaginatedResponse<MasterTableItem>>> {
    return this.get<ApiResponse<PaginatedResponse<MasterTableItem>>>('/list/master/table', params);
  }

  // Add TalentaCards method
  public async getTalentaCards(params?: TalentaCardQueryParams): Promise<ApiResponse<PaginatedResponse<TalentaCardItem>>> {
    return this.get<ApiResponse<PaginatedResponse<TalentaCardItem>>>('/list/master/talenta-cards', params);
  }

  // Add Blog section method
  public async getBlogSection(): Promise<ApiMetaResponse<BlogSectionData>> {
    return this.get<ApiMetaResponse<BlogSectionData>>('/cop/home/landingPage');
  }

  // Add Blog page method
  public async getBlogPage(): Promise<ApiMetaResponse<BlogPageData>> {
    return this.get<ApiMetaResponse<BlogPageData>>('/cop/home/blog');
  }

  public async getStarredAlumni(): Promise<ApiMetaResponse<Alumni[]>> {
    return this.get<ApiMetaResponse<Alumni[]>>('/alumni-profile/starred');
  }

  public async getPublishedAlumni(params?: TalentaCardQueryParams): Promise<ApiMetaResponse<PaginatedProfileResponse<Alumni>>> {
    return this.get<ApiMetaResponse<PaginatedProfileResponse<Alumni>>>('/alumni-profile/published', params);
  }

  public async getProgramBeasiswa(): Promise<ApiMetaResponse<ProgramBeasiswaResponse<ProgramBeasiswa>>> {
    return this.get<ApiMetaResponse<ProgramBeasiswaResponse<ProgramBeasiswa>>>('/alumni-profile/program-beasiswa');
  }

  public async getBidangSektor(): Promise<ApiMetaResponse<ProgramBeasiswaResponse<ProgramBeasiswa>>> {
    return this.get<ApiMetaResponse<ProgramBeasiswaResponse<ProgramBeasiswa>>>('/alumni-profile/bidang-sektor');
  }

  public async searchAlumni(data: SearchAlumniRequest): Promise<ApiMetaResponse<SearchAlumniResponse>> {
    return this.post<ApiMetaResponse<SearchAlumniResponse>>('/alumni-profile/get-by-nib', data);
  }

  public async submitProfileAlumni(data: FormData | SubmitProfileAlumniRequest): Promise<any> {
    // Check if data is FormData (for file upload) or regular object
    if (data instanceof FormData) {
        const response = await this.client.post('/alumni-profile/submit', data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } else {
        return this.post<any>('/alumni-profile/submit', data);
    }
  }

  public async storeMasterTable(data: FormData): Promise<ApiResponse<any>> {
    const response = await this.client.post('/master/table', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  public async updateMasterTable(data: FormData, id: number): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/master/table/${id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // COP methods
  public async getCOPHome(): Promise<ApiResponse<CoPHomeResponse>> {
    return this.get<ApiResponse<CoPHomeResponse>>('/cop/home');
  }

  public async getCOPNews(categoryId?: string): Promise<ApiResponse<CoPNewsItem[]>> {
    const url = categoryId ? `/cop/home/berita/${categoryId}` : '/cop/home/berita/';
    return this.get<ApiResponse<CoPNewsItem[]>>(url);
  }

  /** CoP/Blog article detail (same endpoint as CoP Detail page) */
  public async getCOPDetail(id: string): Promise<ApiResponse<COPDetailData>> {
    return this.get<ApiResponse<COPDetailData>>(`/cop/berita/view/${id}`);
  }

  /** Track share event for CoP/Blog article */
  public async getCOPDetailShare(id: string): Promise<ApiResponse<unknown>> {
    return this.get<ApiResponse<unknown>>(`/cop/berita/share/${id}`);
  }

  // Utility methods
  public async getTimeServer(): Promise<ApiResponse<{ server_time: string }>> {
    return this.get<ApiResponse<{ server_time: string }>>('/server-time');
  }

  // File upload utility
  public async uploadFile(file: File, endpoint: string): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.client.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Add a method to update delay dynamically
  public setSimulateDelay(delay: number): void {
    this.simulateDelay = delay;
  }
}

// Create and export a singleton instance with configurable delay
export const api = new LandingPageApi({
  simulateDelay: process.env.NODE_ENV === 'development' ? 2000 : 0 // 2s delay in development
});

// Export a function to create API instances with custom delays
export const createApiWithDelay = (delay: number) => new LandingPageApi({ simulateDelay: delay });

// Export types for external use
export type { 
  ApiResponse as ApiResponseType, 
  PaginatedResponse as PaginatedResponseType, 
  ApiErrorResponse as ApiErrorResponseType,
  User as UserType, 
  Thesis as ThesisType, 
  ScientificField as ScientificFieldType, 
  Language as LanguageType, 
  ThesisFeedback as ThesisFeedbackType, 
  // Profile as ProfileType,
  MasterTableItem as MasterTableItemType,
  MasterTableQueryParams as MasterTableQueryParamsType,
  TalentaCardItem as TalentaCardItemType,
  TalentaCardQueryParams as TalentaCardQueryParamsType,
  BlogSectionData as BlogSectionDataType,
  BlogPageData as BlogPageDataType,
  BlogQueryParams as BlogQueryParamsType,
  BlogItem as BlogItemType,
  CoPHomeResponse as CoPHomeResponseType,
  CoPNewsItem as CoPNewsItemType,
  COPDetailData as COPDetailDataType,
  COPDetailRelatedArticle as COPDetailRelatedArticleType,
  OverviewResponse as OverviewResponseType,
  ImpactResponse as ImpactResponseType,
  Alumni as AlumniType,
  ProgramBeasiswa as ProgramBeasiswaType,
}; 